import React, { useState, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './DataTable.css';

export default function AllRegistrants() {
  const { showToast } = useToast();
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const limit = 25;

  useEffect(() => {
    setLoading(true);
    api.get('/admin/registrants', { params: { page, limit, search } })
      .then(r => { setRows(r.data.registrants); setTotal(r.data.pagination?.total || 0); })
      .catch(() => showToast('Failed to load registrants', 'error'))
      .finally(() => setLoading(false));
  }, [page, search, showToast]);

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="data-table-page">
        <div className="page-header">
          <div>
            <h1>All Registrants</h1>
            <p>{total.toLocaleString()} total registrant{total !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="toolbar">
          <div className="search-box">
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              placeholder="Search name, email, phone, city…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="table-loading"><div className="spinner" /></div>
          ) : rows.length === 0 ? (
            <div className="empty-state"><p>No registrants found.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Country</th>
                  <th>City</th>
                  <th>Role</th>
                  <th>Events</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id}>
                    <td className="text-muted">{(page - 1) * limit + i + 1}</td>
                    <td>
                      <div className="name-cell">
                        <div className="avatar-sm">{r.firstname?.[0]}{r.lastname?.[0]}</div>
                        <span>{r.firstname} {r.lastname}</span>
                      </div>
                    </td>
                    <td>{r.email}</td>
                    <td>{r.country_code} {r.phone}</td>
                    <td>{r.country || '—'}</td>
                    <td>{r.city || '—'}</td>
                    <td>{r.role || '—'}</td>
                    <td>
                      <span className="badge badge-primary">{r.events_count || 0}</span>
                    </td>
                    <td className="text-muted">
                      {new Date(r.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                    </td>
                    <td>
                      <button className="btn-xs btn-xs-outline" onClick={() => setSelected(r)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const p = page <= 4 ? i + 1 : page + i - 3;
              return p > 0 && p <= totalPages ? p : null;
            }).filter(Boolean).map(p => (
              <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        )}

        {/* Registrant Detail Modal */}
        {selected && (
          <div className="modal-overlay" onClick={() => setSelected(null)}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
              <h3>Registrant Profile</h3>
              <div className="registrant-profile">
                <div className="profile-avatar">
                  {selected.firstname?.[0]}{selected.lastname?.[0]}
                </div>
                <div className="profile-name">{selected.firstname} {selected.lastname}</div>
                <div className="profile-email">{selected.email}</div>
              </div>
              <div className="detail-grid">
                {[
                  ['Phone',        `${selected.country_code} ${selected.phone}`],
                  ['Country',      selected.country],
                  ['City',         selected.city],
                  ['Role',         selected.role],
                  ['Industry',     selected.industry],
                  ['Occupation',   selected.occupation],
                  ['AI Level',     selected.ai_experience_level],
                  ['Heard About',  selected.how_heard],
                  ['Hope to Learn',selected.hope_to_learn],
                ].map(([lbl, val]) => val ? (
                  <div className="detail-row" key={lbl}>
                    <span className="detail-lbl">{lbl}</span>
                    <span className="detail-val">{val}</span>
                  </div>
                ) : null)}
              </div>
              {selected.events_registered && (
                <div style={{marginTop:'1rem'}}>
                  <div className="detail-lbl" style={{marginBottom:'0.5rem'}}>Events Registered</div>
                  {(selected.events_registered || []).map(ev => (
                    <div key={ev.id} style={{padding:'0.4rem 0', borderBottom:'1px solid var(--border)', fontSize:'0.875rem'}}>
                      {ev.title}
                    </div>
                  ))}
                </div>
              )}
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .registrant-profile { text-align:center; padding:1rem 0 1.5rem; }
        .profile-avatar {
          width:56px; height:56px; border-radius:50%;
          background:linear-gradient(135deg, var(--primary-light), var(--accent));
          color:#fff; font-size:1.25rem; font-weight:700;
          display:flex; align-items:center; justify-content:center;
          margin:0 auto 0.75rem; text-transform:uppercase;
        }
        .profile-name { font-family:var(--font-display); font-weight:700; font-size:1.1rem; }
        .profile-email { color:var(--text-muted); font-size:0.875rem; }
        .detail-grid { display:flex; flex-direction:column; gap:0; }
        .detail-row { display:flex; justify-content:space-between; padding:0.5rem 0; border-bottom:1px solid var(--border); gap:1rem; }
        .detail-lbl { font-size:0.78rem; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em; }
        .detail-val { font-size:0.875rem; color:var(--text-primary); text-align:right; }
      `}</style>
    </Layout>
  );
}
